import { Link } from "react-router-dom";
import { Instagram, Youtube, Send, Twitter } from "lucide-react";
import { MaskedLine, MagneticButton } from "./Motion";
import { Logo } from "@/components/Logo";

const socials = [
  { icon: Instagram, label: "Instagram", href: "https://instagram.com/beatkingsmm.in", testid: "footer-social-instagram" },
  { icon: Youtube, label: "YouTube", href: "#top", testid: "footer-social-youtube" },
  { icon: Send, label: "Telegram", href: "#top", testid: "footer-social-telegram" },
  { icon: Twitter, label: "X", href: "#top", testid: "footer-social-twitter" },
];

export default function Footer({ content }) {
  const email = content?.contact_email || "hello@beatkingsmm.com";
  const h1 = content?.footer_headline_1 || "LET'S BUILD";
  const h2 = content?.footer_headline_2 || "VALUE TOGETHER.";
  const text =
    content?.footer_text ||
    "One conversation is all it takes — whether you need a crore impressions, a creator who converts, or a container of product that sells out.";
  return (
    <footer id="contact" data-testid="footer-cta-section" className="relative overflow-hidden pt-32 sm:pt-44">
      <div className="pointer-events-none absolute left-1/2 top-24 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-gold/[0.07] blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6 text-center sm:px-8 lg:px-16">
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold/80" data-testid="footer-eyebrow">
          The finale — your move
        </p>
        <h2
          className="mt-8 text-5xl font-bold leading-[1.02] tracking-tight text-neutral-50 sm:text-7xl lg:text-8xl"
          data-testid="footer-headline"
        >
          <MaskedLine>{h1}</MaskedLine>
          <MaskedLine delay={0.14} innerClassName="font-serif italic font-medium text-gold-grad">
            {h2}
          </MaskedLine>
        </h2>
        <p className="mx-auto mt-8 max-w-xl text-base font-light leading-relaxed text-neutral-400 sm:text-lg">
          {text}
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-5">
          <MagneticButton href={`mailto:${email}`} testid="footer-contact-button">
            Start the Conversation
          </MagneticButton>
          <a
            href={`mailto:${email}`}
            data-testid="footer-email-link"
            className="font-mono text-sm tracking-wide text-neutral-400 underline decoration-gold/40 underline-offset-8 transition-colors duration-300 hover:text-goldlight"
          >
            {email}
          </a>
          <a
            href="https://instagram.com/beatkingsmm.in"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-handle"
            className="font-mono text-sm tracking-wide text-neutral-400 underline decoration-gold/40 underline-offset-8 transition-colors duration-300 hover:text-goldlight"
          >
            @beatkingsmm.in
          </a>
        </div>
      </div>

      <div className="relative mx-auto mt-28 max-w-7xl border-t border-white/5 px-6 py-10 sm:px-8 lg:px-16">
        <div className="flex flex-col items-center justify-between gap-8 md:flex-row">
          <a href="#top" data-testid="footer-logo" className="flex items-center gap-3">
            <Logo
              className="h-8 w-8 rounded-lg object-cover ring-1 ring-gold/40"
            />
            <span className="text-sm font-bold tracking-[0.22em] text-neutral-100">
              BEATKING<span className="text-gold">SMM</span>
            </span>
            <span className="font-mono text-[10px] text-gold/70">2.0</span>
          </a>

          <div className="flex items-center gap-4">
            {socials.map((s) => {
              const Icon = s.icon;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target={s.href.startsWith("http") ? "_blank" : undefined}
                  rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  aria-label={s.label}
                  data-testid={s.testid}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-neutral-400 transition-all duration-300 hover:border-gold/60 hover:text-gold"
                >
                  <Icon size={16} />
                </a>
              );
            })}
          </div>

          <div className="text-center md:text-right">
            <p className="font-mono text-[11px] tracking-[0.2em] text-neutral-600" data-testid="footer-copyright">
              © 2026 BEATKINGSMM · SINCE 2018
            </p>
            <p className="mt-2 font-mono text-[9px] tracking-[0.18em] text-neutral-700" data-testid="footer-disclaimer">
              NOT A TRADING, INVESTING OR MONEY-DOUBLING COMPANY
            </p>
            <Link
              to="/terms"
              data-testid="footer-terms-link"
              className="mt-2 inline-block font-mono text-[9px] tracking-[0.18em] text-neutral-500 underline-offset-4 transition-colors hover:text-gold hover:underline"
            >
              TERMS &amp; CONDITIONS
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
